import { useEffect, useRef, useState } from 'react';
import { AgentEvent, LlmMessage } from '~/atoms/schemas/workflowSchemas';
import type { TaskEntry } from '~/lib/workflows/useWorkflows';
import { ProcessedEvent } from '../types';
import { extractMessagesFromTasks } from './extractMessagesFromTasks';
import { processEventsAndMessages } from './processEventsAndMessages';
import { processLlmMessages } from './processLlmMessages';

// Custom hook to manage conversation state

export const useConversationState = (
  events: AgentEvent[],
  tasks: TaskEntry[],
) => {
  const [processedEvents, setProcessedEvents] = useState<ProcessedEvent[]>([]);
  const [llmMessages, setLlmMessages] = useState<LlmMessage[]>([]);
  const initialLlmMessagesLoaded = useRef(false);

  useEffect(() => {
    // Case 1: When we have streaming events
    if (events.length > 0) {
      // Process and integrate events with existing llm_messages
      const processedArray = processEventsAndMessages(events, llmMessages);
      setProcessedEvents(processedArray);
      return;
    }

    // Case 2: When we don't have streaming events - Initial Load
    if (!initialLlmMessagesLoaded.current && tasks.length > 0) {
      const extractedMessages = extractMessagesFromTasks(tasks);
      setLlmMessages(extractedMessages);
      initialLlmMessagesLoaded.current = true;

      // Process immediately if we have messages
      if (extractedMessages.length > 0) {
        const processedEvents = processLlmMessages(extractedMessages);
        setProcessedEvents(processedEvents);
      }
      return;
    }

    // Case 3: Process existing messages if we have them and don't already have processed events
    if (llmMessages.length > 0 && processedEvents.length === 0) {
      const processedEvents = processLlmMessages(llmMessages);
      setProcessedEvents(processedEvents);
      return;
    }

    // Case 4: Monitor tasks for new messages after initialization
    if (initialLlmMessagesLoaded.current) {
      const extractedMessages = extractMessagesFromTasks(tasks);

      // Only update if we found new messages
      if (extractedMessages.length > llmMessages.length) {
        setLlmMessages(extractedMessages);
      }
    }
  }, [tasks, llmMessages, events, processedEvents.length]);

  return { processedEvents };
};
